/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Container } from 'src/components/Layout'
import FullGameCard from './cards/FullGameCard'
import GameSummary from './cards/GameSummary'
import HighlightCard from './cards/HighlightCard'
import HighlightFilterPills from './filters/HighlightFilterPills'
import LiveScoreWidget from 'src/pages/Game/NewGamePageHeader/LiveScoreWidget'
import VideoEventMarkers, {
  videoContainerMarkerStyles,
} from 'src/pages/Game/NewGamePageHeader/components/VideoEventMarkers'
import VideoPlayer from './player/VideoPlayer'
import { resolveVideoUrl } from '@sportninja/common/utils/assetUrl'
import useGameScoreboard from '@sportninja/common/features/game/hooks/useGameScoreboard'
import useGameJerseyColors from 'src/pages/Game/hooks/useGameJerseyColors'
import colors from '@sportninja/common/constants/appColors'
import Icon from 'src/components/Icon'
import { computeScoreboardAtTime } from '@sportninja/common/utils/games/scoreboardSync'
import {
  containerStyle,
  highlightsListStyle,
  leftPanelStyle,
  rightPanelStyle,
  sectionStyle,
  sectionTitleStyle,
} from './styles'
import { enrichHighlightsWithTeamSide } from './utils'

/**
 * HighlightsV2 component - Main container for the new highlights page
 * @param {Object} game - Game object containing game data
 * @returns {JSX.Element} HighlightsV2 component
 */
const HighlightsV2 = ({ game, highlightsData }) => {
  const videoRef = useRef(null)
  const videoContainerRef = useRef(null)
  const [videoReady, setVideoReady] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const videoCallbackRef = useCallback((node) => {
    videoRef.current = node
    setVideoReady(!!node)
  }, [])

  // Fullscreen the container (not the video) so overlays remain visible
  const toggleFullscreen = useCallback(() => {
    const container = videoContainerRef.current
    if (!container) {
      return
    }
    const fullscreenEl =
      document.fullscreenElement || document.webkitFullscreenElement
    if (fullscreenEl) {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      }
    } else {
      if (container.requestFullscreen) {
        container.requestFullscreen()
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen()
      }
    }
  }, [])

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenEl =
        document.fullscreenElement || document.webkitFullscreenElement
      setIsFullscreen(!!fullscreenEl)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      )
    }
  }, [])

  // State for selected video URL, selected item ID, active filter, full game status, and video offset
  const [selectedVideoUrl, setSelectedVideoUrl] = useState('')
  const [selectedItemId, setSelectedItemId] = useState('')
  const [activeFilter, setActiveFilter] = useState('All')
  const [isFullGameActive, setIsFullGameActive] = useState(true)
  const [videoOffsetSeconds, setVideoOffsetSeconds] = useState(0)

  const { data: gameScoreboardData, refetch: refetchScoreboard } =
    useGameScoreboard({ gameId: game?.id })
  const { homeJerseyColor, visitorJerseyColor } = useGameJerseyColors({
    gameId: game?.id,
    game,
  })
  const scoreboardEvents = gameScoreboardData?.events

  const livestream = selectedVideoUrl
    ? game?.livestreams?.find((ls) => ls.stream_url === selectedVideoUrl)
    : null

  const streamStartedAt = livestream?.stream_started_at

  const initialScoreState = computeScoreboardAtTime({
    currentTimeSec: videoRef.current?.currentTime || 0,
    streamStartedAt,
    isLive: false,
    scoreboardEvents,
  })

  // Enrich highlights with team side information
  const enrichedHighlights = useMemo(() => {
    return enrichHighlightsWithTeamSide(
      highlightsData,
      game?.homeTeamId,
      game?.visitingTeamId
    )
  }, [highlightsData, game?.homeTeamId, game?.visitingTeamId])

  // Filter highlights based on active filter
  const filteredHighlights = useMemo(() => {
    if (activeFilter === 'All') {
      return enrichedHighlights
    }

    // Filter by team side
    if (activeFilter === 'Home' || activeFilter === 'Visitor') {
      return enrichedHighlights.filter(
        (highlight) => highlight.teamSide === activeFilter
      )
    }

    // Filter by type
    return enrichedHighlights.filter(
      (highlight) => highlight.type === activeFilter
    )
  }, [activeFilter, enrichedHighlights])

  // Create full game object from game data
  const fullGame = useMemo(() => {
    if (!game?.broadcast_video_url) {
      return null
    }
    const firstHighlightThumb = enrichedHighlights?.[0]?.thumbnail_url
    return {
      id: 'full-game',
      videoUrl: game.broadcast_video_url,
      thumbnail:
        firstHighlightThumb ||
        '/images/generic_placeholders/placeholder_team.png',
      title: 'Full Game',
      duration: game.duration || 'Live',
    }
  }, [game, enrichedHighlights])

  // Create highlight categories dynamically
  const highlightCategories = useMemo(() => {
    const categories = ['All']

    // Add Home/Visitor if we have highlights
    if (enrichedHighlights.length > 0) {
      const hasHome = enrichedHighlights.some((h) => h.teamSide === 'Home')
      const hasVisitor = enrichedHighlights.some(
        (h) => h.teamSide === 'Visitor'
      )

      if (hasHome) {
        categories.push('Home')
      }
      if (hasVisitor) {
        categories.push('Visitor')
      }
    }

    return categories
  }, [enrichedHighlights])

  // Set default video selection
  useEffect(() => {
    if (game?.broadcast_video_url && !selectedVideoUrl) {
      setSelectedVideoUrl(game.broadcast_video_url)
      setSelectedItemId('full-game')
      setIsFullGameActive(true)
      // Set offset for full game if it exists
      if (game?.broadcast_video_offset_seconds) {
        console.log(
          'Setting default full game offset to:',
          game.broadcast_video_offset_seconds
        )
        setVideoOffsetSeconds(game.broadcast_video_offset_seconds)
      } else {
        setVideoOffsetSeconds(0)
      }
    } else if (
      !game?.broadcast_video_url &&
      enrichedHighlights.length > 0 &&
      !selectedVideoUrl
    ) {
      setSelectedVideoUrl(enrichedHighlights[0].video_url)
      setSelectedItemId(enrichedHighlights[0].id)
      setIsFullGameActive(false)
      setVideoOffsetSeconds(0)
    }
  }, [game?.broadcast_video_url, enrichedHighlights, selectedVideoUrl])

  // Handle card clicks
  const handleCardClick = (item) => {
    if (item.videoUrl) {
      setSelectedVideoUrl(item.videoUrl)
      setSelectedItemId(item.id)
      setIsFullGameActive(item.id === 'full-game')
      // Set offset for full game if it exists
      if (item.id === 'full-game' && game?.broadcast_video_offset_seconds) {
        console.log(
          'Setting full game offset to:',
          game.broadcast_video_offset_seconds
        )
        setVideoOffsetSeconds(game.broadcast_video_offset_seconds)
      } else {
        setVideoOffsetSeconds(0)
      }
    } else if (item.video_url) {
      setSelectedVideoUrl(item.video_url)
      setSelectedItemId(item.id)
      setIsFullGameActive(false)
      setVideoOffsetSeconds(0)
    }
  }

  // Handle filter changes
  const handleFilterChange = (filter) => {
    setActiveFilter(filter)
  }

  // Check if a card is currently playing
  const isCardPlaying = (item) => {
    return selectedItemId === item.id
  }

  return (
    <Container
      noWrap
      css={css`
        width: 100%;
        margin-bottom: 16px;
        @media (max-width: 768px) {
          margin-left: 0;
          margin-bottom: 60px;
        }
      `}
    >
      <div css={containerStyle}>
        {/* Left Panel - Video Player */}
        <div css={leftPanelStyle}>
          <div
            ref={videoContainerRef}
            onDoubleClick={toggleFullscreen}
            css={css`
              position: relative;
              overflow: hidden;
              background: ${colors.BLACK};
              border-radius: 8px;

              &:fullscreen,
              &:-webkit-full-screen {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
              }

              /* Best-effort: hide native fullscreen button where supported */
              video::-webkit-media-controls-fullscreen-button {
                display: none;
              }
            `}
          >
            <div
              css={css`
                position: relative;
                width: 100%;
                height: ${isFullscreen ? 'auto' : '100%'};
                max-width: ${isFullscreen ? 'calc(100vh * 16 / 9)' : 'none'};
                max-height: ${isFullscreen ? '100vh' : 'none'};
                aspect-ratio: 16 / 9;
                ${videoContainerMarkerStyles};
              `}
            >
              <VideoPlayer
                ref={videoCallbackRef}
                url={resolveVideoUrl(selectedVideoUrl)}
                offsetSeconds={videoOffsetSeconds}
              />
              {isFullGameActive && (
                <LiveScoreWidget
                  game={game}
                  videoUrl={selectedVideoUrl}
                  homeTeamName={game?.homeTeam?.fullName || game?.homeTeamName}
                  visitorTeamName={
                    game?.visitingTeam?.fullName || game?.visitingTeamName
                  }
                  videoRef={videoRef}
                  videoReady={videoReady}
                  isLive={false}
                  scoreboardEvents={scoreboardEvents}
                  initialScoreState={initialScoreState}
                  refetchScoreboard={refetchScoreboard}
                  homeJerseyColor={homeJerseyColor}
                  visitorJerseyColor={visitorJerseyColor}
                />
              )}
              {isFullGameActive && (
                <VideoEventMarkers
                  videoRef={videoRef}
                  scoreboardEvents={scoreboardEvents}
                  streamStartedAt={streamStartedAt}
                  isLive={false}
                />
              )}

              {/* Custom fullscreen button */}
              <button
                onClick={toggleFullscreen}
                css={css`
                  position: absolute;
                  bottom: 70px;
                  right: 16px;
                  z-index: 10;
                  background-color: rgba(0, 0, 0, 0.6);
                  border: none;
                  border-radius: 8px;
                  border: 1px solid ${colors.NEUTRAL_100};
                  width: 40px;
                  height: 40px;
                  cursor: pointer;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: ${colors.WHITE};

                  &:hover {
                    background-color: rgba(0, 0, 0, 0.8);
                  }
                `}
              >
                <Icon
                  name={isFullscreen ? 'compress' : 'expand'}
                  color={colors.WHITE}
                  fontSize={24}
                />
              </button>
            </div>
          </div>
          {/* <div css={progressPlaceholderStyle}>
            Video progress controls will be implemented here
          </div> */}
          <GameSummary
            summary={
              game?.game_summary
                ? {
                    title: game.game_summary.headline,
                    content: game.game_summary.summary,
                  }
                : null
            }
          />
        </div>

        {/* Right Panel - Cards and Highlights */}
        <div css={rightPanelStyle}>
          {/* Full Game Section */}
          {fullGame && (
            <div css={sectionStyle}>
              <div css={sectionTitleStyle}>Full Game</div>
              <FullGameCard
                game={fullGame}
                onClick={handleCardClick}
                isPlaying={isCardPlaying(fullGame)}
              />
            </div>
          )}

          {/* Highlights Section */}
          <div css={sectionStyle}>
            <div css={sectionTitleStyle}>Highlights</div>
            <HighlightFilterPills
              categories={highlightCategories}
              activeFilter={activeFilter}
              onFilterChange={handleFilterChange}
            />
            <div css={highlightsListStyle}>
              {filteredHighlights.length > 0 ? (
                filteredHighlights.map((highlight) => (
                  <HighlightCard
                    key={highlight.id}
                    highlight={highlight}
                    onClick={handleCardClick}
                    isPlaying={isCardPlaying(highlight)}
                  />
                ))
              ) : (
                <div
                  css={css`
                    padding: 24px;
                    text-align: center;
                    color: #666;
                  `}
                >
                  No highlights available yet
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Container>
  )
}

export default HighlightsV2
